import { Ref } from "@vue/composition-api";
import { ContentPriceSubject, ContentPurchase } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { yMetrics } from "@/lib/analytics";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { handleRequestError } from "@/lib/handleRequestError";
import PaymentManager from "@/payments";
import { PurchaseDetails, PurchaseResponse } from "@/payments/types";
import { ERRORS, EVENTS } from "@/const";

interface IOneTimePurchase {
  success: boolean;
  response?: PurchaseResponse;
  purchaseDetails?: ContentPurchase;
}

export default function useOneTimePurchase(data: Ref<ContentPriceSubject>, purchaseDetails: Ref<PurchaseDetails>) {
  const purchaseByCard = async (): Promise<IOneTimePurchase> => {
    let success = false;
    let contentPurchaseResponse: ContentPurchase | undefined = undefined;
    const response = await PaymentManager.purchase(purchaseDetails.value);
    if (response.status && response.subjectPurchased) {
      success = true;
      yMetrics.paymentPurchase();
    }
    // case for two-steps payment systems
    if (response.status && !response.subjectPurchased) {
      contentPurchaseResponse = await providers.ContentPriceProvider.purchase({ data: data.value });
      success = true;
      yMetrics.paymentPurchase();
    }
    return {
      success,
      response,
      purchaseDetails: contentPurchaseResponse,
    };
  };

  const purchaseWithBalance = async (): Promise<IOneTimePurchase> => {
    let success = false;
    let contentPurchaseResponse: ContentPurchase | undefined = undefined;
    try {
      providers.ContentPriceProvider.setReceiveErrors(false);
      contentPurchaseResponse = await providers.ContentPriceProvider.purchase({ data: data.value });
      success = true;
      yMetrics.purchaseFromBalance();
    } catch (e) {
      handleRequestError(e, [
        {
          comparator: e => e.details?.messageTemplate === ERRORS.SPENDING_BALANCE,
          onSuccessCB: () => emitter.emit(EVENTS.BALANCE_REFILL_SHOW),
        },
      ]);
    }
    return {
      success,
      purchaseDetails: contentPurchaseResponse,
    };
  };

  const performOneTimePurchase = (fromBalance: boolean) => {
    return fromBalance ? purchaseWithBalance() : purchaseByCard();
  };

  return {
    performOneTimePurchase,
  };
}
